import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "@phosphor-icons/react";
import MerchantLayout, { StatusPill } from "@/components/MerchantLayout";
import { mApi } from "@/lib/api";
import { merchantCardsApi } from "@/lib/merchantCards";
import { ApplyCardModal, BindCardModal } from "@/pages/m/MerchantCardModals";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function MerchantCards() {
  const [items, setItems] = useState([]);
  const [showApply, setShowApply] = useState(false);
  const [showBind, setShowBind] = useState(false);

  const load = () => merchantCardsApi.list().then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  return (
    <MerchantLayout>
      <div className="p-6 lg:p-10" data-testid="m-cards-page">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Cards</h1>
                <p className="text-sm text-zinc-500">Issue and manage cards for your cardholders ({items.length}).</p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowBind(true)} className="m-btn-outline">Bind physical</button>
                <button type="button" onClick={() => setShowApply(true)} data-testid="m-apply-card-btn" className="m-btn-dark">
                  <Plus size={14} weight="bold" /> Apply for card
                </button>
              </div>
            </div>
            <Surface>
              <Table cols={["Card", "Type", "Status", "Expiry", "Balance", "Created", ""]}>
                {items.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-50 hover:bg-zinc-50/60">
                    <td className="px-5 py-3 font-mono text-xs">{c.masked_pan || c.id.slice(0, 12)}</td>
                    <td className="px-5 py-3 capitalize">{c.type}</td>
                    <td className="px-5 py-3"><StatusPill value={c.status} /></td>
                    <td className="px-5 py-3 font-mono text-xs">{c.expiry || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(c.balance)}</td>
                    <td className="px-5 py-3 text-xs text-zinc-500">{(c.created_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-3 text-right">
                      <Link to={`/m/cards/${c.id}`} className="text-xs font-medium text-zinc-700 hover:text-zinc-950 underline-offset-2 hover:underline">Manage</Link>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-zinc-500">No cards yet. Apply for a virtual card or bind a physical card to get started.</td></tr>
                )}
              </Table>
            </Surface>
          </div>
      {showApply && <ApplyCardModal onClose={() => setShowApply(false)} onSuccess={() => { setShowApply(false); load(); }} />}
      {showBind && <BindCardModal onClose={() => setShowBind(false)} onSuccess={() => { setShowBind(false); load(); }} />}
    </MerchantLayout>
  );
}

export function MerchantUsers() {
  const [items, setItems] = useState([]);
  useEffect(() => { mApi.get("/merchant/users").then((r) => setItems(r.data.items)); }, []);
  return (
    <MerchantLayout>
      <div className="p-6 lg:p-10" data-testid="m-users-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Cardholders</h1>
        <p className="text-sm text-zinc-500 mb-6">{items.length} cardholders.</p>
        <Surface>
          <Table cols={["Name", "Email", "Phone", "KYC", "Created"]}>
            {items.map((u) => (
              <tr key={u.id} className="border-b border-zinc-50">
                <td className="px-5 py-3 font-medium">{u.first_name} {u.last_name}</td>
                <td className="px-5 py-3 text-xs">{u.email}</td>
                <td className="px-5 py-3 text-xs">{u.phone || "—"}</td>
                <td className="px-5 py-3">
                  <Link to={`/m/users/${u.id}/kyc`} className="hover:underline"><StatusPill value={u.kyc_status} /></Link>
                </td>
                <td className="px-5 py-3 text-xs text-zinc-500">{(u.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-zinc-500">No cardholders yet.</td></tr>}
          </Table>
        </Surface>
      </div>
    </MerchantLayout>
  );
}

export function MerchantTransactions() {
  const [items, setItems] = useState([]);
  useEffect(() => { mApi.get("/merchant/transactions").then((r) => setItems(r.data.items)); }, []);
  return (
    <MerchantLayout>
      <div className="p-6 lg:p-10" data-testid="m-tx-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Transactions</h1>
        <p className="text-sm text-zinc-500 mb-6">Live ledger ({items.length}).</p>
        <Surface>
          <Table cols={["When", "Type", "POS", "Amount", "Status"]}>
            {items.map((t) => (
              <tr key={t.id} className="border-b border-zinc-50">
                <td className="px-5 py-3 text-xs font-mono text-zinc-500">{(t.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-5 py-3"><span className="text-[10px] font-mono uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-sm bg-zinc-100">{t.type}</span></td>
                <td className="px-5 py-3 text-xs">{t.merchant_name || "—"}</td>
                <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(t.amount)}</td>
                <td className="px-5 py-3"><StatusPill value={t.status} /></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-zinc-500">No transactions yet.</td></tr>}
          </Table>
        </Surface>
      </div>
    </MerchantLayout>
  );
}

export function MerchantAudit() {
  const [items, setItems] = useState([]);
  useEffect(() => { mApi.get("/merchant/audit-logs").then((r) => setItems(r.data.items)); }, []);
  return (
    <MerchantLayout>
      <div className="p-6 lg:p-10" data-testid="m-audit-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Audit log</h1>
        <p className="text-sm text-zinc-500 mb-6">Append-only record of every privileged action.</p>
        <Surface>
          <Table cols={["When", "Action", "Method", "Path", "IP", "Status"]}>
            {items.map((a) => (
              <tr key={a.id} className="border-b border-zinc-50">
                <td className="px-5 py-3 text-xs font-mono text-zinc-500">{(a.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-5 py-3 font-mono text-xs">{a.action}</td>
                <td className="px-5 py-3"><span className="text-[10px] font-mono uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-sm bg-zinc-100">{a.method}</span></td>
                <td className="px-5 py-3 font-mono text-xs text-zinc-600">{a.path}</td>
                <td className="px-5 py-3 font-mono text-xs">{a.ip_address || "—"}</td>
                <td className="px-5 py-3 font-mono text-xs">{a.status_code}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500">No audit entries yet.</td></tr>}
          </Table>
        </Surface>
      </div>
    </MerchantLayout>
  );
}

export function MerchantUsage() {
  const [data, setData] = useState({ items: [], top_paths: [] });
  useEffect(() => { mApi.get("/merchant/usage").then((r) => setData(r.data)); }, []);
  return (
    <MerchantLayout>
      <div className="p-6 lg:p-10" data-testid="m-usage-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">API Usage</h1>
        <p className="text-sm text-zinc-500 mb-6">Per-call usage history with latency.</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Surface className="lg:col-span-2 overflow-hidden">
            <Table cols={["When", "Method", "Path", "Status", "Latency", "IP"]}>
              {data.items.slice(0, 200).map((u, i) => (
                <tr key={`${u.ts}-${i}`} className="border-b border-zinc-50">
                  <td className="px-5 py-3 text-xs font-mono text-zinc-500">{(u.ts || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-5 py-3 text-xs font-mono">{u.method}</td>
                  <td className="px-5 py-3 text-xs font-mono text-zinc-600">{u.path}</td>
                  <td className="px-5 py-3 text-xs font-mono">{u.status_code}</td>
                  <td className="px-5 py-3 text-right text-xs font-mono tabular-nums">{u.latency_ms} ms</td>
                  <td className="px-5 py-3 text-xs font-mono">{u.ip || "—"}</td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500">No API calls yet.</td></tr>}
            </Table>
          </Surface>
          <Surface className="p-5">
            
              <div className="font-display text-lg tracking-tight font-medium mb-4">Top endpoints</div>
              <ul className="space-y-2">
                {(data.top_paths || []).map((p) => (
                  <li key={p.path} className="flex justify-between text-sm">
                    <span className="font-mono text-xs text-zinc-700 truncate flex-1 mr-3">{p.path}</span>
                    <span className="font-mono tabular-nums">{p.count}</span>
                  </li>
                ))}
                {(!data.top_paths || data.top_paths.length === 0) && <li className="text-xs text-zinc-500">No data.</li>}
              </ul>
            
          </Surface>
        </div>
      </div>
    </MerchantLayout>
  );
}

function Surface({ children, className = "" }) {
  return <div className={`bg-white border border-zinc-200 rounded-sm overflow-hidden ${className}`}>{children}</div>;
}
function Table({ cols, children }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
          {cols.map((c) => <th key={c} className={`px-5 py-3 ${c === "Amount" || c === "Balance" || c === "Latency" ? "text-right" : ""}`}>{c}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}
